import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Heart, Eye, Zap, Shield } from "lucide-react";
import T from "../components/EditableText";

const VALUES = [
    { icon: Eye, title: "Radikale Transparenz", text: "Klare Kommunikation, ehrliche Einschätzungen, transparente Preise — immer." },
    { icon: Heart, title: "Mensch zuerst", text: "Lebensläufe sind Geschichten. Jeder Hire ist eine langfristige Beziehung." },
    { icon: Zap, title: "Speed mit Substanz", text: "Schnell ohne hektisch zu sein. Gründlich ohne langsam zu werden." },
    { icon: Shield, title: "Diskretion", text: "Was uns anvertraut wird, bleibt vertraulich. Punkt." },
];

const TEAM = [
    {
        name: "Daniel Herter",
        role: "Founder & Managing Partner",
        bio: "Gründer von aimploy. Verbindet tiefes Branchen-Verständnis mit KI-gestütztem Sourcing — und besetzt Schlüsselrollen in unter 8 Wochen.",
        img: "https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
];

const TIMELINE = [
    { year: "2022", title: "Gegründet in Berlin", text: "Drei Co-Founder, ein WeWork-Schreibtisch, eine Mission." },
    { year: "2023", title: "100. Hire platziert", text: "Erster großer Meilenstein. Erste Erweiterung des Teams." },
    { year: "2024", title: "AI-Sourcing Plattform", text: "Eigenes Tool für datenbasiertes Talent Mapping geht live." },
    { year: "2025", title: "DACH-weit aktiv", text: "Büros in München und Wien. 25-köpfiges Team." },
    { year: "2026", title: "500+ erfolgreiche Hires", text: "Heute. Die Reise hat gerade erst begonnen." },
];

export default function UeberUns() {
    return (
        <div data-testid="ueber-uns-page">
            {/* HERO */}
            <section className="relative bg-aimploy-bg pt-24 lg:pt-32 pb-24 lg:pb-32 overflow-hidden">
                <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-aimploy-violet/10 blur-3xl" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-7">
                            <span className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">
                                Über uns
                            </span>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold leading-[0.9] tracking-tight mt-6">
                                Wir sind keine<br />
                                Recruiter.<br />
                                <span className="italic font-medium text-aimploy-violet">Wir sind Match-Maker.</span>
                            </h1>
                        </div>
                        <p className="lg:col-span-5 text-lg lg:text-xl text-aimploy-muted leading-relaxed">
                            aimploy entstand aus dem Frust mit klassischem Recruiting. Mit Headhuntern, die Stellenanzeigen schalten. Mit
                            Beratungen, die fünfstellig kassieren und keinen einzigen passiven Kandidaten ansprechen. Wir machen es anders.
                        </p>
                    </div>
                </div>
            </section>

            {/* MISSION */}
            <section className="py-24 lg:py-32 bg-aimploy-dark text-white relative overflow-hidden grain">
                <div className="absolute inset-0 bg-dots-dark opacity-30" />
                <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12">
                    <T k="ueber.mission.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Mission</T>
                    <T k="ueber.mission.body" as="p" className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold leading-[1.05] mt-6">
                        Wir glauben, dass jede Schlüsselrolle eine Person verdient, die sie wirklich will — und ein Team, das sie wirklich braucht. Unsere Aufgabe ist es, diese beiden zusammenzubringen. Schneller. Präziser. Ehrlicher.
                    </T>
                </div>
            </section>

            {/* VALUES */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-16">
                        <T k="ueber.values.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Werte</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="ueber.values.title">{`Vier Prinzipien.\nKeine Verhandlung.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {VALUES.map((v, i) => (
                            <div key={v.title} className="bg-white rounded-2xl p-8 border border-black/5 hover:-translate-y-1 transition-all">
                                <div className="h-12 w-12 rounded-xl bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center mb-6">
                                    <v.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-display text-xl font-bold mb-3"><T k={`ueber.value.${i}.title`}>{v.title}</T></h3>
                                <T k={`ueber.value.${i}.text`} as="p" className="text-aimploy-muted text-sm leading-relaxed">{v.text}</T>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TEAM */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-16">
                        <T k="ueber.team.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Team</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="ueber.team.title">{`Die Köpfe\ndahinter.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-md md:max-w-none mx-auto">
                        {TEAM.length === 1 && <div className="hidden md:block" />}
                        {TEAM.map((t, i) => (
                            <div key={t.name} className="group">
                                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-aimploy-bg mb-5 relative">
                                    <img src={t.img} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-aimploy-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                                        <a href="https://www.linkedin.com/company/aimploy-ug" target="_blank" rel="noopener noreferrer" className="text-white flex items-center gap-2 text-sm font-semibold">
                                            <Linkedin className="w-4 h-4" /> LinkedIn
                                        </a>
                                    </div>
                                </div>
                                <h3 className="font-display text-2xl font-bold"><T k={`ueber.member.${i}.name`}>{t.name}</T></h3>
                                <div className="text-aimploy-violet text-sm font-semibold mt-1"><T k={`ueber.member.${i}.role`}>{t.role}</T></div>
                                <T k={`ueber.member.${i}.bio`} as="p" className="text-aimploy-muted text-sm mt-3 leading-relaxed">{t.bio}</T>
                            </div>
                        ))}
                        {TEAM.length === 1 && <div className="hidden md:block" />}
                    </div>
                </div>
            </section>

            {/* TIMELINE */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[900px] mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <T k="ueber.timeline.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Geschichte</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="ueber.timeline.title">Unser Weg.</T>
                        </h2>
                    </div>

                    <div className="relative pl-8 lg:pl-0">
                        <div className="absolute left-3 lg:left-1/2 top-0 bottom-0 w-px bg-aimploy-navy/10 lg:-translate-x-px" />
                        {TIMELINE.map((m, i) => (
                            <div key={m.year} className={`relative mb-12 lg:flex lg:items-center ${i % 2 === 0 ? "" : "lg:flex-row-reverse"}`}>
                                <div className="absolute left-3 lg:left-1/2 top-2 w-3 h-3 rounded-full bg-aimploy-violet -translate-x-1.5 ring-4 ring-aimploy-bg" />
                                <div className={`lg:w-1/2 ${i % 2 === 0 ? "lg:pr-12 lg:text-right" : "lg:pl-12"}`}>
                                    <div className="font-display text-3xl font-bold text-aimploy-violet"><T k={`ueber.milestone.${i}.year`}>{m.year}</T></div>
                                    <h3 className="font-display text-2xl font-bold mt-1 mb-2"><T k={`ueber.milestone.${i}.title`}>{m.title}</T></h3>
                                    <T k={`ueber.milestone.${i}.text`} as="p" className="text-aimploy-muted">{m.text}</T>
                                </div>
                                <div className="hidden lg:block lg:w-1/2" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
                    <h2 className="font-display text-5xl lg:text-7xl font-bold leading-[0.95] max-w-4xl mx-auto">
                        <T k="ueber.cta.title">{`Lust, mit uns\nzu arbeiten?`}</T>
                    </h2>
                    <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 mt-10 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-10 py-5 rounded-full text-base font-semibold hover:scale-[1.02] transition-all"
                    >
                        <T k="ueber.cta.button">Kontakt aufnehmen</T> <ArrowUpRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
